/**
 * Meta Pixel event helpers
 *
 * Safely wraps fbq() calls so the app never crashes if the pixel
 * hasn't loaded yet (e.g. ad blockers, native iOS webview).
 *
 * Every track call emits a unique `eventID` so Meta can deduplicate
 * against the matching CAPI (server) event. For Purchase, the same
 * `event_id` is shared between browser and server by writing the UUID
 * into the Stripe Checkout Session metadata in `createCheckoutSession.js`
 * (server reads `session.metadata.clientEventId` / `subscription.metadata.clientEventId`).
 *
 * Pixel IDs (set via env):
 *   SvgTrace:        1638659957203847
 *   EmbroideryTrace: 1669858414329579
 */

import { trackTikTokEvent, trackTikTokPageView } from "./tiktokPixel";

const SUBSCRIPTION_PRICES = {
  designer: 12.99,
  designerYearly: 95.88,
  monthly: 12.99,
  yearly: 95.88,
  lifetime: 197.0,
  embroidery: 17.99,
  embroideryYearly: 119.88,
};

const COUPON_FIXED_DISCOUNTS = {
  WELCOME50: 48.88,
};

const PURCHASE_EVENT_ID_KEY = "pendingCheckoutEventId";

function fbq(...args) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
  }
}

/**
 * Generate a unique event ID for a given event type. Uses crypto.randomUUID
 * when available, falls back to a timestamp + random string.
 *
 * @param {string} prefix - short event-type prefix (e.g. "pv", "vc", "ic")
 * @returns {string}
 */
export function newEventId(prefix = "evt") {
  let uuid;
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      uuid = crypto.randomUUID();
    }
  } catch (_) {
    // fall through
  }
  if (!uuid) {
    uuid = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
  return `${prefix}_${uuid}`;
}

/**
 * Collect the identifiers Meta uses to match a server-side (CAPI) event back
 * to an ad click. Server events fired long after checkout — a trial converting
 * five days later, for example — have no browser to read these from, so they
 * are stashed in Stripe metadata at checkout and replayed by the webhook.
 *
 * `_fbc` is only written once Meta's script has seen an `fbclid`, so rebuild
 * it from the URL when the cookie isn't there yet.
 */
export function getMetaMatchData() {
  if (typeof document === "undefined") return {};

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[2]) : undefined;
  };

  let fbc = readCookie("_fbc");
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  // Stripe caps metadata values at 500 characters.
  const clamp = (value) => (value ? String(value).slice(0, 500) : undefined);

  return {
    fbp: readCookie("_fbp"),
    fbc,
    userAgent: clamp(navigator.userAgent),
    sourceUrl: clamp(window.location.href),
  };
}

/**
 * Fire on SPA route changes so Meta counts in-app navigation.
 * The initial PageView is handled by the base snippet in index.html.
 */
export function trackPageView() {
  const eventID = newEventId("pv");
  fbq("track", "PageView", {}, { eventID });
  trackTikTokPageView();
  return eventID;
}

/**
 * Fire when the paywall / pricing screen is shown, before the user
 * selects a plan. Useful for mid-funnel retargeting audiences.
 * @param {string} action - the feature that triggered the paywall
 */
export function trackViewContent(action = "paywall") {
  const eventID = newEventId("vc");
  fbq("track", "ViewContent", { content_name: action }, { eventID });
  trackTikTokEvent(
    "ViewContent",
    {
      content_type: "product",
      contents: [
        {
          content_id: action,
          content_name: action,
          content_type: "product",
          quantity: 1,
        },
      ],
      description: action,
    },
    eventID
  );
  return eventID;
}

/**
 * Fire when a new user completes account creation.
 *
 * Includes `value: 0` + `currency` so Meta can parse the event for
 * future value-based optimization (per audit recommendation).
 *
 * @param {string} method - 'email' | 'google' | 'apple'
 */
export function trackSignUp(method = "email") {
  const eventID = newEventId("reg");
  fbq(
    "track",
    "CompleteRegistration",
    {
      content_name: method,
      value: 0,
      currency: "USD",
    },
    { eventID }
  );
  trackTikTokEvent(
    "CompleteRegistration",
    {
      contents: [
        {
          content_id: `registration_${method}`,
          content_name: `Registration (${method})`,
          content_type: "product",
          quantity: 1,
          price: 0,
        },
      ],
      content_type: "product",
      description: method,
      value: 0,
      currency: "USD",
    },
    eventID
  );
  return eventID;
}

/**
 * Fire when a user starts a free trial (EmbroideryTrace).
 * Supplements CompleteRegistration with a StartTrial event so Meta can
 * build a "trial started" audience for retargeting and lookalikes.
 *
 * @param {string} planType - 'embroidery' | 'embroideryYearly'
 */
export function trackStartTrial(planType = "embroidery") {
  const predictedLtv = SUBSCRIPTION_PRICES[planType] ?? 24.99;
  const eventID = newEventId("st");
  fbq(
    "track",
    "StartTrial",
    {
      value: 0,
      currency: "USD",
      predicted_ltv: predictedLtv,
      content_name: planType,
    },
    { eventID }
  );
  trackTikTokEvent(
    "StartTrial",
    {
      content_type: "product",
      contents: [
        {
          content_id: planType,
          content_name: planType,
          content_type: "product",
          quantity: 1,
          price: 0,
        },
      ],
      description: planType,
      value: 0,
      currency: "USD",
    },
    eventID
  );
  return eventID;
}

/**
 * Fire when a user successfully converts an image to a stitch file.
 * High-intent signal for retargeting non-subscribers.
 *
 * @param {string} format - e.g. 'dst', 'pes', 'jef'
 */
export function trackFileConversion(format = "dst") {
  const eventID = newEventId("fc");
  fbq(
    "track",
    "InitiateCheckout",
    {
      content_name: `Embroidery File Conversion (${format.toUpperCase()})`,
      currency: "USD",
    },
    { eventID }
  );
  trackTikTokEvent(
    "Download",
    {
      contents: [
        {
          content_id: `embroidery_${format.toLowerCase()}`,
          content_name: `Embroidery File Conversion (${format.toUpperCase()})`,
          content_type: "product",
          quantity: 1,
          price: 0,
        },
      ],
      content_type: "product",
      description: `Embroidery File Conversion (${format.toUpperCase()})`,
      value: 0,
      currency: "USD",
    },
    eventID
  );
  return eventID;
}

/**
 * Fire when a user opens the upgrade/paywall screen and selects a plan.
 * @param {string} subscriptionType - e.g. 'designer' | 'designerYearly'
 */
export function trackInitiateCheckout(subscriptionType) {
  const value = SUBSCRIPTION_PRICES[subscriptionType] ?? 0;
  const eventID = newEventId("ic");
  fbq(
    "track",
    "InitiateCheckout",
    {
      value,
      currency: "USD",
      content_name: subscriptionType,
    },
    { eventID }
  );
  trackTikTokEvent(
    "InitiateCheckout",
    {
      content_type: "product",
      contents: [
        {
          content_id: subscriptionType,
          content_name: subscriptionType,
          content_type: "product",
          quantity: 1,
          price: value,
        },
      ],
      description: subscriptionType,
      value,
      currency: "USD",
    },
    eventID
  );
  return eventID;
}

/**
 * Fire when payment is confirmed.
 *
 * Web/Stripe path: pass no argument — reads the type + shared event_id
 *   stored in localStorage by createCheckoutSession.js before the Stripe
 *   redirect. That event_id was also written to the Stripe session metadata,
 *   so the CAPI webhook uses the identical value → Meta deduplicates.
 *   Only one-off payments call this. Subscriptions are reported server-side
 *   from the paid invoice, because a trial checkout collects $0 today and the
 *   real charge lands days later when no browser is present.
 *
 * iOS/RevenueCat path: pass subscriptionType directly. Optionally pass an
 *   override eventId that the caller previously persisted to Firestore so
 *   the RevenueCat webhook can use the same value as the browser pixel.
 *
 * @param {string|null} subscriptionType - optional override (for iOS)
 * @param {string|null} overrideEventId - optional override event_id (for iOS)
 */
export function trackPurchase(subscriptionType = null, overrideEventId = null) {
  // Session-level dedup: prevent the Purchase event from firing more than once
  // per checkout (e.g. on page refresh while checkout_success is still in URL).
  const dedupKey = "metaPixelPurchaseFired";
  if (sessionStorage.getItem(dedupKey)) {
    return null;
  }

  const resolvedType =
    subscriptionType || localStorage.getItem("pendingCheckoutType") || "";
  let value = SUBSCRIPTION_PRICES[resolvedType] ?? 0;

  // Apply fixed-amount coupon discount if one was used during checkout
  const coupon = localStorage.getItem("pendingCheckoutCoupon");
  if (coupon && COUPON_FIXED_DISCOUNTS[coupon]) {
    value = Math.round((value - COUPON_FIXED_DISCOUNTS[coupon]) * 100) / 100;
    if (value < 0) value = 0;
  }

  if (!resolvedType || value === 0) {
    console.warn(
      "[MetaPixel] trackPurchase fired with unknown subscription type — value will be $0.",
      { subscriptionType, resolvedType }
    );
  }

  // Shared event_id: prefer override (iOS), then the UUID we stored before
  // the Stripe redirect, otherwise generate a fresh one. Using the shared id
  // is what allows Meta to dedupe browser vs CAPI.
  const eventID =
    overrideEventId ||
    localStorage.getItem(PURCHASE_EVENT_ID_KEY) ||
    newEventId("purchase");

  fbq(
    "track",
    "Purchase",
    {
      value,
      currency: "USD",
      content_name: resolvedType,
    },
    { eventID }
  );
  trackTikTokEvent(
    "Purchase",
    {
      content_type: "product",
      contents: [
        {
          content_id: resolvedType,
          content_name: resolvedType,
          content_type: "product",
          quantity: 1,
          price: value,
        },
      ],
      description: resolvedType,
      value,
      currency: "USD",
    },
    eventID
  );

  sessionStorage.setItem(dedupKey, "1");

  if (!subscriptionType) {
    localStorage.removeItem("pendingCheckoutType");
    localStorage.removeItem("pendingCheckoutCoupon");
    localStorage.removeItem(PURCHASE_EVENT_ID_KEY);
  }

  return eventID;
}
